import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import {
  StatusBadge,
  PriorityBadge,
} from "@/components/Badges";

import {
  STATUS_META,
  HISTORY_LABELS,
  RECURRENCE_LABELS,
  fmtDate,
  fmtDateTime,
} from "@/lib/constants";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { cn } from "@/lib/utils";

import {
  ArrowLeft,
  Loader2,
  Play,
  CheckCircle2,
  Send,
  MessageSquare,
  Paperclip,
  Building2,
  Calendar,
  Clock,
  User,
  XCircle,
  ThumbsUp,
  History,
  Repeat,
  AlertCircle,
  Trash2,
  Pencil,
  Save,
} from "lucide-react";

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const isAdmin = user?.role === "admin";

  const [comment, setComment] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    assignee_id: "",
    department_id: "",
    date: "",
    time: "",
    due_date: "",
    due_time: "",
    priority: "normal",
    requires_evidence: false,
    requires_approval: false,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["task", id],
    queryFn: () => api.get(`/tasks/${id}`).then((r) => r.data),
    retry: false,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.get("/employees").then((r) => r.data),
    enabled: isAdmin,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: () => api.get("/departments").then((r) => r.data),
    enabled: isAdmin,
  });

  if (isError) {
    const status = error?.response?.status;

    return (
      <div
        className="mx-auto flex max-w-md flex-col items-center py-20 text-center"
        data-testid="task-error"
      >
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
          <AlertCircle className="h-7 w-7 text-red-500" />
        </div>

        <h2 className="font-heading text-xl font-bold text-slate-900">
          {status === 403
            ? "Sin acceso a esta tarea"
            : status === 404
              ? "Tarea no encontrada"
              : "Ocurrió un error"}
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          {status === 403
            ? "Esta tarea pertenece a otro funcionario."
            : "No pudimos cargar la tarea."}
        </p>

        <Button
          onClick={() => navigate(-1)}
          className="mt-5 rounded-full"
          data-testid="task-error-back"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Volver
        </Button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const { task, history, comments } = data;

  const m = STATUS_META[task.status] || STATUS_META.pendiente;

  const mine = task.assignee?.id === user?.id;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["task", id] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const act = async (fn, msg) => {
    setBusy(true);

    try {
      await fn();
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(
        apiError(e?.response?.data?.detail)
      );
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      title: task.title || "",
      description: task.description || "",
      assignee_id:
        task.assignee_id ||
        task.assignee?.id ||
        "",
      department_id:
        task.department_id ||
        task.department?.id ||
        "",
      date: task.date || "",
      time: task.time || "",
      due_date: task.due_date || task.date || "",
      due_time: task.due_time || "",
      priority: task.priority || "normal",
      requires_evidence: !!task.requires_evidence,
      requires_approval: !!task.requires_approval,
    });

    setEditOpen(true);
  };

  const updateField = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) {
      toast.error("El título no puede estar vacío");
      return;
    }

    if (!editForm.assignee_id) {
      toast.error("Selecciona un funcionario");
      return;
    }

    if (!editForm.department_id) {
      toast.error("Selecciona un área");
      return;
    }

    if (!editForm.date) {
      toast.error("Selecciona la fecha de la tarea");
      return;
    }

    if (!editForm.time) {
      toast.error("Selecciona la hora de inicio");
      return;
    }

    if (!editForm.due_date) {
      toast.error("Selecciona la fecha de vencimiento");
      return;
    }

    if (!editForm.due_time) {
      toast.error("Selecciona la hora de vencimiento");
      return;
    }

    setBusy(true);

    try {
      await api.put(`/tasks/${id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        assignee_id: editForm.assignee_id,
        department_id: editForm.department_id,
        date: editForm.date,
        time: editForm.time,
        due_date: editForm.due_date,
        due_time: editForm.due_time,
        priority: editForm.priority,
        requires_evidence: editForm.requires_evidence,
        requires_approval: editForm.requires_approval,
      });

      toast.success("Tarea actualizada correctamente");

      setEditOpen(false);

      await qc.invalidateQueries({
        queryKey: ["task", id],
      });

      await qc.invalidateQueries({
        queryKey: ["tasks"],
      });
    } catch (e) {
      toast.error(
        apiError(e?.response?.data?.detail)
      );
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;

    await act(
      () =>
        api.post(`/tasks/${id}/comments`, {
          text: comment,
        }),
      "Comentario agregado"
    );

    setComment("");
  };

  const addEvidence = async () => {
    if (!evidence.trim()) return;

    await act(
      () =>
        api.post(`/tasks/${id}/evidence`, {
          comment: evidence,
          kind: "nota",
        }),
      "Evidencia agregada"
    );

    setEvidence("");
  };

  const doReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Indica el motivo del rechazo");
      return;
    }

    await act(
      () =>
        api.post(`/tasks/${id}/reject`, {
          reason: rejectReason,
        }),
      "Tarea rechazada"
    );

    setRejectOpen(false);
    setRejectReason("");
  };

  const doDelete = async () => {
    setBusy(true);

    try {
      await api.delete(`/tasks/${id}`);

      toast.success("Tarea eliminada correctamente");

      setDeleteOpen(false);

      await qc.invalidateQueries({
        queryKey: ["tasks"],
      });

      navigate("/tareas");
    } catch (e) {
      toast.error(
        apiError(e?.response?.data?.detail)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mx-auto max-w-3xl space-y-5"
      data-testid="task-detail"
    >
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver
      </button>

      {/* INFORMACIÓN DE LA TAREA */}
      <div
        className={cn(
          "rounded-2xl border border-slate-200 border-l-4 bg-white p-5 shadow-sm",
          m.leftBorder
        )}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <StatusBadge status={task.status} />

          <PriorityBadge priority={task.priority} />

          {task.recurrence !== "none" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
              <Repeat className="h-3 w-3" />
              {RECURRENCE_LABELS[task.recurrence]}
            </span>
          )}
        </div>

        <h1 className="font-heading text-2xl font-extrabold text-slate-900">
          {task.title}
        </h1>

        {task.description && (
          <p className="mt-2 text-sm text-slate-600">
            {task.description}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <Meta
            icon={User}
            label="Funcionario"
            value={task.assignee?.name || "—"}
          />

          <Meta
            icon={Building2}
            label="Área"
            value={task.department?.name || "—"}
          />

          <Meta
            icon={Calendar}
            label="Fecha"
            value={fmtDate(task.date)}
          />

          <Meta
            icon={Clock}
            label="Vence"
            value={`${fmtDate(task.due_date)} ${task.due_time}`}
          />
        </div>

        {task.rejection_reason && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />

            <div>
              <span className="font-bold">
                Rechazada:
              </span>{" "}
              {task.rejection_reason}
            </div>
          </div>
        )}
      </div>

      {/* ACCIONES DEL ADMINISTRADOR */}
      {isAdmin && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            onClick={openEdit}
            disabled={busy}
            className="flex-1 rounded-full bg-slate-900 py-6 font-bold hover:bg-slate-800"
            data-testid="edit-task-btn"
          >
            <Pencil className="mr-2 h-5 w-5" />
            Editar tarea
          </Button>

          <Button
            onClick={() => setDeleteOpen(true)}
            disabled={busy}
            variant="outline"
            className="flex-1 rounded-full border-red-300 py-6 font-bold text-red-600 hover:bg-red-50"
            data-testid="delete-btn"
          >
            <Trash2 className="mr-2 h-5 w-5" />
            Eliminar
          </Button>
        </div>
      )}

      {/* ACCIONES DEL FUNCIONARIO */}
      {mine && (
        <div className="space-y-3">
          {task.status === "pendiente" && (
            <Button
              onClick={() =>
                act(
                  () =>
                    api.post(
                      `/tasks/${id}/start`
                    ),
                  "Tarea iniciada"
                )
              }
              disabled={busy}
              data-testid="start-task-btn"
              className="w-full rounded-full bg-blue-500 py-6 text-base font-bold hover:bg-blue-600 active:scale-95"
            >
              <Play className="mr-2 h-5 w-5" />
              Iniciar Tarea
            </Button>
          )}

          {(task.status === "en_progreso" ||
            task.status === "vencida") && (
            <Button
              onClick={() =>
                act(
                  () =>
                    api.post(
                      `/tasks/${id}/complete`,
                      { comment: "" }
                    ),
                  task.requires_approval
                    ? "Enviada a revisión"
                    : "Tarea completada"
                )
              }
              disabled={busy}
              data-testid="complete-task-btn"
              className="w-full rounded-full bg-emerald-500 py-6 text-lg font-bold shadow-lg shadow-emerald-500/30 hover:bg-emerald-600 active:scale-95"
            >
              {task.requires_approval ? (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Enviar a Revisión
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                  Completar Tarea
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* APROBACIÓN ADMINISTRATIVA */}
      {isAdmin &&
        task.status === "en_revision" && (
          <div className="flex gap-3">
            <Button
              onClick={() =>
                act(
                  () =>
                    api.post(
                      `/tasks/${id}/approve`
                    ),
                  "Tarea aprobada"
                )
              }
              disabled={busy}
              data-testid="approve-btn"
              className="flex-1 rounded-full bg-emerald-500 py-6 font-bold hover:bg-emerald-600 active:scale-95"
            >
              <ThumbsUp className="mr-2 h-5 w-5" />
              Aprobar
            </Button>

            <Button
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              variant="outline"
              data-testid="reject-btn"
              className="flex-1 rounded-full border-red-300 py-6 font-bold text-red-600 hover:bg-red-50 active:scale-95"
            >
              <XCircle className="mr-2 h-5 w-5" />
              Rechazar
            </Button>
          </div>
        )}

      {/* EVIDENCIAS */}
      {task.requires_evidence && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-slate-900">
            <Paperclip className="h-4 w-4" />
            Evidencias
          </h3>

          <div className="space-y-2">
            {(task.evidence || []).map((ev) => (
              <div
                key={ev.id}
                className="rounded-lg bg-slate-50 p-3 text-sm"
              >
                <div className="text-slate-700">
                  {ev.comment}
                </div>

                <div className="mt-1 text-xs text-slate-400">
                  {ev.by} · {fmtDateTime(ev.created_at)}
                </div>
              </div>
            ))}

            {(!task.evidence ||
              task.evidence.length === 0) && (
              <p className="text-sm text-slate-400">
                Aún no hay evidencias.
              </p>
            )}
          </div>

          {mine && (
            <div className="mt-3 flex gap-2">
              <Textarea
                value={evidence}
                onChange={(e) =>
                  setEvidence(e.target.value)
                }
                placeholder="Describe la evidencia"
                rows={2}
                data-testid="evidence-input"
              />

              <Button
                onClick={addEvidence}
                disabled={busy}
                className="rounded-lg"
                data-testid="add-evidence-btn"
              >
                Adjuntar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* COMENTARIOS */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-slate-900">
          <MessageSquare className="h-4 w-4" />
          Comentarios
        </h3>

        <div className="space-y-2">
          {comments.map((c) => (
            <div
              key={c.id}
              className="rounded-lg bg-slate-50 p-3 text-sm"
            >
              <div className="font-semibold text-slate-700">
                {c.author_name}
              </div>

              <div className="text-slate-600">
                {c.text}
              </div>

              <div className="mt-0.5 text-xs text-slate-400">
                {fmtDateTime(c.created_at)}
              </div>
            </div>
          ))}

          {comments.length === 0 && (
            <p className="text-sm text-slate-400">
              Sin comentarios.
            </p>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <Textarea
            value={comment}
            onChange={(e) =>
              setComment(e.target.value)
            }
            placeholder="Escribe un comentario"
            rows={2}
            data-testid="comment-input"
          />

          <Button
            onClick={addComment}
            disabled={busy}
            className="rounded-lg"
            data-testid="add-comment-btn"
          >
            Enviar
          </Button>
        </div>
      </div>

      {/* HISTORIAL */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 font-heading font-bold text-slate-900">
          <History className="h-4 w-4" />
          Historial y trazabilidad
        </h3>

        <ol className="relative space-y-4 border-l-2 border-slate-100 pl-5">
          {history.map((h) => (
            <li
              key={h.id}
              className="relative"
              data-testid={`history-${h.action}`}
            >
              <span className="absolute -left-[27px] top-0.5 h-3 w-3 rounded-full border-2 border-white bg-slate-800" />

              <div className="text-sm font-semibold text-slate-800">
                {HISTORY_LABELS[h.action] ||
                  h.action}
              </div>

              <div className="text-xs text-slate-500">
                {h.actor_name} ·{" "}
                {fmtDateTime(h.timestamp)}
              </div>

              {h.detail && (
                <div className="text-xs text-slate-400">
                  {h.detail}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* DIALOG EDITAR */}
      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!busy) setEditOpen(open);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Editar tarea
            </DialogTitle>

            <DialogDescription>
              Modifica los datos de la tarea y guarda
              los cambios.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* TÍTULO */}
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Título *
              </label>

              <input
                type="text"
                value={editForm.title}
                onChange={(e) =>
                  updateField(
                    "title",
                    e.target.value
                  )
                }
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                placeholder="Título de la tarea"
                disabled={busy}
              />
            </div>

            {/* DESCRIPCIÓN */}
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Descripción
              </label>

              <textarea
                value={editForm.description}
                onChange={(e) =>
                  updateField(
                    "description",
                    e.target.value
                  )
                }
                rows={3}
                className="mt-1 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                placeholder="Descripción de la tarea"
                disabled={busy}
              />
            </div>

            {/* FUNCIONARIO + ÁREA */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Funcionario *
                </label>

                <select
                  value={editForm.assignee_id}
                  onChange={(e) =>
                    updateField(
                      "assignee_id",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                >
                  <option value="">
                    Selecciona un funcionario
                  </option>

                  {employees
                    .filter(
                      (emp) =>
                        emp.active !== false
                    )
                    .map((emp) => (
                      <option
                        key={emp.id}
                        value={emp.id}
                      >
                        {emp.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Área *
                </label>

                <select
                  value={editForm.department_id}
                  onChange={(e) =>
                    updateField(
                      "department_id",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                >
                  <option value="">
                    Selecciona un área
                  </option>

                  {departments.map((dept) => (
                    <option
                      key={dept.id}
                      value={dept.id}
                    >
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* FECHA + HORA */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Fecha *
                </label>

                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) =>
                    updateField(
                      "date",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Hora de inicio *
                </label>

                <input
                  type="time"
                  value={editForm.time}
                  onChange={(e) =>
                    updateField(
                      "time",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* VENCIMIENTO */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Fecha de vencimiento *
                </label>

                <input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) =>
                    updateField(
                      "due_date",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Hora de vencimiento *
                </label>

                <input
                  type="time"
                  value={editForm.due_time}
                  onChange={(e) =>
                    updateField(
                      "due_time",
                      e.target.value
                    )
                  }
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* PRIORIDAD */}
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Prioridad
              </label>

              <select
                value={editForm.priority}
                onChange={(e) =>
                  updateField(
                    "priority",
                    e.target.value
                  )
                }
                disabled={busy}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="normal">
                  Normal
                </option>

                <option value="alta">
                  Alta
                </option>

                <option value="urgente">
                  Urgente
                </option>
              </select>
            </div>

            {/* OPCIONES */}
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={
                    editForm.requires_evidence
                  }
                  onChange={(e) =>
                    updateField(
                      "requires_evidence",
                      e.target.checked
                    )
                  }
                  disabled={busy}
                  className="h-4 w-4 rounded"
                />

                <span className="text-sm font-medium text-slate-700">
                  Requiere evidencia
                </span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={
                    editForm.requires_approval
                  }
                  onChange={(e) =>
                    updateField(
                      "requires_approval",
                      e.target.checked
                    )
                  }
                  disabled={busy}
                  className="h-4 w-4 rounded"
                />

                <span className="text-sm font-medium text-slate-700">
                  Requiere aprobación
                </span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>

            <Button
              onClick={saveEdit}
              disabled={busy}
              className="bg-slate-900 font-bold hover:bg-slate-800"
              data-testid="save-edit-task-btn"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}

              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG RECHAZAR */}
      <Dialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
      >
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>
              Rechazar tarea
            </DialogTitle>

            <DialogDescription>
              Indica el motivo para devolver la tarea
              al funcionario.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={rejectReason}
            onChange={(e) =>
              setRejectReason(e.target.value)
            }
            placeholder="Motivo del rechazo"
            rows={3}
            data-testid="reject-reason-input"
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setRejectOpen(false)
              }
              disabled={busy}
            >
              Cancelar
            </Button>

            <Button
              onClick={doReject}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-reject-btn"
            >
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG ELIMINAR */}
      <Dialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      >
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>
              Eliminar tarea
            </DialogTitle>

            <DialogDescription>
              ¿Estás seguro de que deseas eliminar esta
              tarea? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <strong>{task.title}</strong>

            <br />

            La tarea y su información asociada serán
            eliminadas.
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteOpen(false)
              }
              disabled={busy}
            >
              Cancelar
            </Button>

            <Button
              onClick={doDelete}
              disabled={busy}
              className="bg-red-600 font-bold hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}

              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 text-slate-400" />

      <div>
        <div className="text-[11px] font-medium text-slate-400">
          {label}
        </div>

        <div className="font-semibold text-slate-700">
          {value}
        </div>
      </div>
    </div>
  );
}